import { QueryClient, dehydrate } from "@tanstack/react-query";
import Ad from "src/components/ads/Ad";
import ContentWrapper from "src/components/contentwrapper/ContentWrapper";
import withWatchlist from "src/components/filmwebid/withWatchlist";
import FilmwebNews from "src/components/filmwebnews/FilmwebNews";
import PopularMoviesList, { POP_MOVIES_QUERY } from "src/components/popularmovieslist/PopularMoviesList";
import TrailereSeo from "src/components/seo/TrailereSeo";
import StreamingSearchBox from "src/components/streaming/StreamingSearchBox";
import theme from "src/components/themes/DefaultTheme";
import TrailerPoster from "src/components/trailerposter/TrailerPoster";
import TrailerPosterListExpander from "src/components/trailerposterlistexpander/TrailerPosterListExpander";
import { gql } from "graphql-request";
import { buildFilminfoQueryKey, filminfoRequest } from "src/lib/client/useFilminfo";
import AdWrapper from "src/lib/contexts/AdContext";
import { FRONTPAGE_NEWS, filterOtherNews } from "src/lib/groq";
import { MovieClip } from "src/lib/movieinfo/movieinfotypes";
import { setDefaultCacheHeaders } from "src/lib/server/cache";
import { filminfoQuery } from "src/lib/server/filminfo";
import { getServerData, sanityQuery, type ICommonProps } from "src/lib/server/sanity";
import styled from "src/lib/styles/css";
import IFilmnytt from "src/lib/types/Filmnytt";
import { type GetServerSideProps } from "next";
import { useOverriddenAdunits } from "src/lib/server/ads";

//#region [Other] getServerSideProps
export const getServerSideProps: GetServerSideProps = getServerData(async (context) => {
    setDefaultCacheHeaders(context);

    const queryClient = new QueryClient();
    const prefetchPopMoviesPromise = queryClient.prefetchQuery({
        queryKey: buildFilminfoQueryKey(POP_MOVIES_QUERY),
        queryFn: filminfoRequest
    });

    const otherNewsQueryPromise = sanityQuery<IFilmnytt[]>(FRONTPAGE_NEWS)

    const newTrailersPromise = filminfoQuery(TRAILERS_QUERY, { numNewTrailers: 108 });

    const [otherNews, newTrailers] = await Promise.all([otherNewsQueryPromise, newTrailersPromise, prefetchPopMoviesPromise]);

    return {
        props: {
            newTrailers: newTrailers?.movieQuery?.getNewestTrailers ?? null,
            otherNews: filterOtherNews(otherNews)?.slice(0, 3) ?? null,
            dehydratedState: dehydrate(queryClient)
        }
    }

});
//#endregion

//#region [Props]
type TrailereProps = {
    newTrailers: MovieClip[] | null;
    otherNews: IFilmnytt[] | null;
} & ICommonProps;
//#endregion


//#region [Component]
export default function Trailere({ appData, newTrailers, otherNews, adUnits }: TrailereProps) {
    const finalAdUnits = useOverriddenAdunits(ADS, adUnits);
    return <ContentWrapper as="section">
        <TrailereSeo />
        <SSectionHeading>Nyeste trailere</SSectionHeading>
        <AdWrapper ads={finalAdUnits}>
            <STrailerGrid>
                <TrailerPosterListExpander>
                    {newTrailers?.map(clip => <WLTrailerPoster key={clip.id} movieClip={clip} />)}
                </TrailerPosterListExpander>
            </STrailerGrid>

            <SAd targetId="FW_midt1_trailerseksjon" />

            <FilmwebNews articles={otherNews} />

            <SAd targetId="FW_midt2_trailerseksjon" />
        </AdWrapper>
        <SPopularMoviesList />
        <SStreamingSearchBox showLogo toolbox={appData?.streamingToolbox} />
    </ContentWrapper>
}
//#endregion

const WLTrailerPoster = withWatchlist(TrailerPoster, "movieClip");

const TRAILERS_QUERY = gql`
query ($numNewTrailers: Int) {
	movieQuery {
		getNewestTrailers(numTrailers: $numNewTrailers) {
			__typename
	  		clipTitle
	  		movieTitle
      		id
      		movieId
			sanityImagePosterUrl
    	}
	}
}
`;

const ADS = [
    { auId: '0000000000053768', auW: '2000', auH: '600', targetId: 'FW_topp_trailerseksjon' },
    { auId: '0000000000053790', auW: '2000', auH: '600', targetId: 'FW_midt1_trailerseksjon' },
    { auId: '000000000005379f', auW: '2000', auH: '600', targetId: 'FW_midt2_trailerseksjon' },
    { auId: '00000000000537ba', auW: '2000', auH: '600', targetId: 'FW_midt3_trailerseksjon' },
];

//#region [Styles]

const SAd = styled(Ad)`
	margin-bottom: 30px;
	margin-top: 30px;
`;

const SSectionHeading = styled.h1`
	text-align: left;
	font-size: 24px;
	font-weight: bold;
	color: var(--textcolor);
	line-height: 1.42;
	padding-bottom: 0;

	padding-top: 30px;
	margin-bottom: 10px;
	font-size: 40px; // Final fallback to something in the middle
	font-size: calc(24px + 1.5vw); // fluid scale, min 24 px
	font-size: min(max(24px, 4vw), 54px);// fluid scale, min 24 px, max 54px.
	font-size: clamp(24px, 4vw, 54px); // same version as above, but easier to read

	padding-left: ${theme.maxContentPadding};
	padding-right: ${theme.maxContentPadding};

	@media ${theme.mq.desktop} {
		font-size: 54px;
		line-height: 60px;
		padding-bottom: calc(10px + 1vh);

	}
`;

const STrailerGrid = styled.div`
	max-width: 100vw;
	display: grid;
	row-gap: 30px;
	column-gap: 10px;

	justify-items: stretch;
	align-items: stretch;
	// NOTE: https://css-tricks.com/preventing-a-grid-blowout/
	// Tl;DR: min width is auto, which can cause blowout. minax(0, 1fr) sets min size to 0 preventing blowout
	grid-template-columns: repeat(3, minmax(0, 1fr));
	grid-template-rows: auto;
	grid-auto-rows: auto;
	margin-bottom: 30px;

	padding: 0 ${theme.maxContentPadding};

	@media ${theme.mq.desktop} {
		max-width: 100%;
		grid-row-gap: 60px;
		row-gap: 50px;
		row-gap: min(50px, 4.5vw);
		grid-template-columns: repeat(6, minmax(0, 1fr));
		column-gap: 50px;
		column-gap: min(50px, 4.5vw);
		margin-bottom: 50px;
		padding: 0;
	}
`;

const SPopularMoviesList = styled(PopularMoviesList)`
	margin-left: ${theme.maxContentPadding};
	margin-top: 50px;

	@media ${theme.mq.desktop} {
		margin-left: 0;
	}

`;

const SStreamingSearchBox = styled(StreamingSearchBox)`
	margin: 50px 0;
`;


//#endregion